import React, { useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowDown,
  faArrowUp,
  faFilter,
  faGripVertical,
  faRotateLeft,
  faSearch,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import Modal from "./Modal";

const GridFilterConfigurator = ({
  isOpen,
  onClose,
  fields,
  preference,
  onSave,
  onReset,
  saving = false,
}) => {
  const { t } = useTranslation();
  const [items, setItems] = useState([]);
  const [draggedKey, setDraggedKey] = useState(null);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    const byKey = new Map(fields.map((field) => [field.key, field]));
    const orderedKeys = [
      ...(preference?.fieldOrder || []).filter((key) => byKey.has(key)),
      ...fields.map((field) => field.key).filter(
        (key) => !(preference?.fieldOrder || []).includes(key),
      ),
    ];
    const hidden = new Set(preference?.hiddenFields || []);
    const hasCustomPreference =
      (preference?.fieldOrder || []).length > 0 ||
      (preference?.hiddenFields || []).length > 0;
    setItems(orderedKeys.map((key) => ({
      field: byKey.get(key),
      visible: hasCustomPreference
        ? !hidden.has(key)
        : (byKey.get(key)?.showInFilter ?? byKey.get(key)?.filterConfig?.show ?? true),
    })));
    setSearch("");
  }, [isOpen, fields, preference]);

  const move = (index, direction) => {
    const target = index + direction;
    if (target < 0 || target >= items.length) return;
    setItems((current) => {
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const dropBefore = (targetKey) => {
    if (!draggedKey || draggedKey === targetKey) return;
    setItems((current) => {
      const next = [...current];
      const sourceIndex = next.findIndex(({ field }) => field.key === draggedKey);
      const targetIndex = next.findIndex(({ field }) => field.key === targetKey);
      if (sourceIndex < 0 || targetIndex < 0) return current;
      const [moved] = next.splice(sourceIndex, 1);
      next.splice(targetIndex, 0, moved);
      return next;
    });
    setDraggedKey(null);
  };

  const translatedLabel = (field) => {
    const label =
      field.filterLabel ??
      field.filterConfig?.label ??
      field.label ??
      field.key;
    return typeof label === "string" && label.includes(".")
      ? t(label)
      : label;
  };

  const tableLabel = (field) => {
    const explicitLabel = field.tableLabel || field.sourceJoin || field.sourceTable || field.table;
    if (explicitLabel) {
      if (typeof explicitLabel === "string" && explicitLabel.includes(".")) {
        return t(explicitLabel);
      }
      if (String(explicitLabel).toLowerCase() === "salesline") {
        return t("salesLines.title");
      }
      return explicitLabel;
    }
    const key = String(field.key || "");
    const keyTable = key.includes(".") ? key.split(".")[0] : "";
    return keyTable.toLowerCase() === "salesline" ? t("salesLines.title") : keyTable;
  };

  const filteredItems = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const indexedItems = items.map((item, index) => ({ ...item, index }));
    if (!needle) {
      return indexedItems;
    }
    return indexedItems.filter(({ field }) => {
      const label = translatedLabel(field);
      return `${label} ${tableLabel(field)} ${field.key}`.toLowerCase().includes(needle);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, search, t]);

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t("filterPreferences.title")}
      icon={faFilter}
      className="grid-column-config-modal"
    >
      <div className="grid-column-config-search">
        <FontAwesomeIcon icon={faSearch} />
        <input
          type="search"
          className="form-control"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("filterPreferences.searchPlaceholder")}
          autoComplete="off"
        />
      </div>
      <div className="grid-column-config-list">
        {filteredItems.map(({ field, visible, index }) => (
          <div
            key={field.key}
            className="grid-column-config-row"
            draggable
            onDragStart={() => setDraggedKey(field.key)}
            onDragOver={(event) => event.preventDefault()}
            onDrop={() => dropBefore(field.key)}
          >
            <FontAwesomeIcon icon={faGripVertical} className="grid-column-drag-handle" />
            <input
              type="checkbox"
              checked={visible}
              onChange={(event) => {
                const checked = event.target.checked;
                setItems((current) => current.map((item) =>
                  item.field.key === field.key ? { ...item, visible: checked } : item
                ));
              }}
              aria-label={translatedLabel(field)}
            />
            <span className="grid-column-config-label">
              {tableLabel(field) && (
                <span className="grid-column-config-table">{tableLabel(field)}</span>
              )}
              <span className="grid-column-config-label-text">{translatedLabel(field)}</span>
            </span>
            <button
              type="button"
              className="btn btn-default btn-icon"
              onClick={() => move(index, -1)}
              disabled={index === 0}
              title={t("gridPreferences.moveUp")}
            >
              <FontAwesomeIcon icon={faArrowUp} />
            </button>
            <button
              type="button"
              className="btn btn-default btn-icon"
              onClick={() => move(index, 1)}
              disabled={index === items.length - 1}
              title={t("gridPreferences.moveDown")}
            >
              <FontAwesomeIcon icon={faArrowDown} />
            </button>
          </div>
        ))}
        {filteredItems.length === 0 && (
          <div className="grid-column-config-empty">
            {t("filterPreferences.noSearchResults")}
          </div>
        )}
      </div>

      <div className="modal-footer grid-column-config-actions">
        <button type="button" className="btn btn-default" onClick={onReset} disabled={saving}>
          <FontAwesomeIcon icon={faRotateLeft} /> {t("gridPreferences.restoreDefaults")}
        </button>
        <button type="button" className="btn btn-secondary" onClick={onClose} disabled={saving}>
          {t("common.cancel")}
        </button>
        <button
          type="button"
          className="btn btn-primary"
          disabled={saving || !items.some((item) => item.visible)}
          onClick={() => onSave({
            fieldOrder: items.map(({ field }) => field.key),
            hiddenFields: items.filter((item) => !item.visible).map(({ field }) => field.key),
          })}
        >
          {t("common.save")}
        </button>
      </div>
    </Modal>
  );
};

export default GridFilterConfigurator;
